import type React from 'react';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { AlertTriangle, GitBranch, Pencil, Route, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Button, EmptyState } from '../../../components/ui/index.js';
import { getWorkflowOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { WorkflowBuilderPage } from '../WorkflowBuilder.js';
import { useDaemonScope } from './DaemonScopeContext.js';
import { daemonReads } from './daemon-queries.js';
import type { DaemonRecord } from '../../../generated/api/types.gen.js';

interface DaemonWorkflowTabProps {
  daemon: DaemonRecord;
}

interface WorkflowAssignment {
  id: number;
  workflowDefinitionId: number;
  purpose?: string | null;
  executionOrder?: number | null;
  enabled?: boolean | null;
  workflowCatalogVersionId?: number | null;
}

export const DaemonWorkflowTab: React.FC<DaemonWorkflowTabProps> = ({ daemon }) => {
  const { scope } = useDaemonScope();
  const reads = daemonReads(scope);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);

  const assignmentsQuery = useQuery(reads.workflows(daemon.id));
  const recentRunsQuery = useQuery(reads.runs(daemon.id, { limit: '50' }));

  const assignments = normalizeAssignments(assignmentsQuery.data);
  // Each assigned workflow is fetched by id (the workflow list is paged, so a name lookup against it misses).
  const workflowQueries = useQueries({
    queries: assignments.map((assignment) =>
      getWorkflowOptions({ path: { id: String(assignment.workflowDefinitionId) } }),
    ),
  });
  const workflowsById = new Map(
    assignments.flatMap((assignment, index) => {
      const template = workflowQueries[index]?.data?.template;
      return template ? [[assignment.workflowDefinitionId, template] as const] : [];
    }),
  );
  const workflowLabel = (assignment: WorkflowAssignment): string => {
    const template = workflowsById.get(assignment.workflowDefinitionId);
    if (template) return template.name;
    const index = assignments.indexOf(assignment);
    return workflowQueries[index]?.isError ? 'Workflow unavailable' : 'Loading workflow...';
  };
  const selectedAssignment =
    assignments.find((assignment) => assignment.id === selectedAssignmentId) ??
    preferredAssignment(assignments) ??
    null;
  const selectedWorkflow = selectedAssignment ? workflowsById.get(selectedAssignment.workflowDefinitionId) : undefined;
  const recentRuns = recentRunsQuery.data?.data ?? [];
  const selectedWorkflowRuns = selectedAssignment
    ? recentRuns.filter((run) => Number(run.workflowDefinitionId) === Number(selectedAssignment.workflowDefinitionId))
    : [];

  const assignmentStats = useMemo(
    () => ({
      total: assignments.length,
      enabled: assignments.filter((assignment) => assignment.enabled !== false).length,
      primary: assignments.filter((assignment) => (assignment.purpose ?? 'primary') === 'primary').length,
    }),
    [assignments],
  );

  if (assignmentsQuery.isLoading) {
    return <div className="rounded-lg border bg-surface p-6 text-sm text-muted">Loading workflow assignments...</div>;
  }

  if (assignmentsQuery.isError) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger-muted p-4 text-sm text-danger">
        <AlertTriangle size={14} className="shrink-0" />
        <span>Could not load workflow assignments.</span>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <EmptyState
        icon={Workflow}
        title="No workflow assigned"
        description="Assign a workflow before judging this daemon's execution quality."
      />
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-heading">
              <Workflow size={15} /> Daemon Workflow
            </h3>
            <p className="mt-1 text-sm text-muted">
              Inspect and edit the workflow that defines this daemon's behavior, step contracts, nested workflow/task
              calls, and latest run quality signals.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="neutral">{assignmentStats.total} assigned</Badge>
            <Badge variant="success">{assignmentStats.enabled} enabled</Badge>
            <Badge variant={assignmentStats.primary > 0 ? 'info' : 'warning'}>{assignmentStats.primary} primary</Badge>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {assignments.map((assignment) => {
            const active = assignment.id === selectedAssignment?.id;
            return (
              <Button
                variant="ghost"
                aria-pressed={active}
                key={assignment.id}
                onClick={() => setSelectedAssignmentId(assignment.id)}
                className={`h-auto flex-col items-start whitespace-normal border px-3 py-2 text-left text-sm ${
                  active ? 'border-accent bg-accent/10 text-heading' : 'bg-surface-alt text-muted hover:text-heading'
                }`}
              >
                <div className="flex items-center gap-2">
                  <GitBranch size={13} />
                  <span className="font-medium">{workflowLabel(assignment)}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                  <span>{assignment.purpose ?? 'primary'}</span>
                  {assignment.workflowCatalogVersionId && <span>from catalog</span>}
                  {assignment.enabled === false && <span>disabled</span>}
                </div>
              </Button>
            );
          })}
        </div>
      </section>

      {selectedAssignment && (
        <section className="rounded-lg border bg-surface p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-heading">{workflowLabel(selectedAssignment)}</h3>
                <Badge variant="neutral">{selectedAssignment.purpose ?? 'primary'}</Badge>
                {selectedWorkflow?.status && <Badge variant="info">{selectedWorkflow.status}</Badge>}
              </div>
              <p className="mt-1 text-xs text-muted">
                Use this editor to verify whether the daemon persona, source extraction, integrity checks, and nested
                review calls are actually represented as workflow steps.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Link
                to={`/agents/workflows/${selectedAssignment.workflowDefinitionId}/edit`}
                className="inline-flex items-center gap-1 text-accent-text hover:underline"
              >
                <Pencil size={13} /> Full editor
              </Link>
              <Link
                to={`/agents/workflows/${selectedAssignment.workflowDefinitionId}`}
                className="inline-flex items-center gap-1 text-accent-text hover:underline"
              >
                <Route size={13} /> Workflow detail
              </Link>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-muted md:grid-cols-4">
            <span>Steps: {selectedWorkflow ? selectedWorkflow.steps.length : '...'}</span>
            <span>Status: {selectedAssignment.enabled === false ? 'Disabled' : 'Enabled'}</span>
            <span>Order: {selectedAssignment.executionOrder ?? 0}</span>
            <span>
              Latest run:{' '}
              {recentRunsQuery.isLoading
                ? '...'
                : selectedWorkflowRuns[0]?.startedAt
                  ? new Date(selectedWorkflowRuns[0].startedAt).toLocaleString()
                  : 'No runs yet'}
            </span>
          </div>
        </section>
      )}

      {selectedAssignment ? (
        <div className="h-[860px] min-h-[640px] overflow-hidden rounded-lg border bg-bg p-3">
          <WorkflowBuilderPage panelMode idOverride={String(selectedAssignment.workflowDefinitionId)} />
        </div>
      ) : null}
    </div>
  );
};

function normalizeAssignments(input: unknown): WorkflowAssignment[] {
  const data = asRecord(input).data ?? input;
  if (!Array.isArray(data)) return [];
  return data
    .filter((item): item is Record<string, unknown> =>
      Boolean(item && typeof item === 'object' && !Array.isArray(item)),
    )
    .map((item) => ({
      id: Number(item.id),
      workflowDefinitionId: Number(item.workflowDefinitionId),
      purpose: typeof item.purpose === 'string' ? item.purpose : 'primary',
      executionOrder: typeof item.executionOrder === 'number' ? item.executionOrder : 0,
      enabled: typeof item.enabled === 'boolean' ? item.enabled : true,
      workflowCatalogVersionId:
        typeof item.workflowCatalogVersionId === 'number' ? item.workflowCatalogVersionId : null,
    }))
    .filter((item) => Number.isFinite(item.id) && Number.isFinite(item.workflowDefinitionId));
}

function preferredAssignment(assignments: WorkflowAssignment[]): WorkflowAssignment | null {
  return (
    assignments.find((assignment) => assignment.enabled !== false && (assignment.purpose ?? 'primary') === 'primary') ??
    assignments.find((assignment) => assignment.enabled !== false) ??
    assignments[0] ??
    null
  );
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}
